/**
 * Audio messaging utilities for WebSocket communication
 * Handles creation of enhanced live audio stream messages and legacy formats
 */

/**
 * Create an enhanced live audio stream message for optimal Chirp performance
 * @param {string} base64Audio - Base64 encoded PCM audio data
 * @param {number} sequence - Sequence number for ordering
 * @param {Object} chunkInfo - Audio chunk information
 * @param {Object} sessionInfo - Session information
 * @param {Object} format - Audio format details
 * @returns {Object} Enhanced live audio stream message
 */
export const createEnhancedAudioStreamMessage = ({
  base64Audio,
  sequence,
  chunkInfo = {},
  sessionInfo = {},
  format = {
    encoding: 'pcm_s16le',
    sample_rate: 16000,
    channels: 1,
    bit_depth: 16
  }
}) => {
  return {
    type: "live_audio_stream",
    audio: base64Audio,
    sequence: sequence,
    format: {
      encoding: format.encoding || "pcm_s16le",
      sample_rate: format.sample_rate || 16000,
      channels: format.channels || 1,
      bit_depth: format.bit_depth || 16
    },
    chunk_info: {
      is_interim: chunkInfo.is_interim !== undefined ? chunkInfo.is_interim : true,
      is_final_chunk: chunkInfo.is_final_chunk || false,
      duration_ms: chunkInfo.duration_ms || 2000,
      has_speech: chunkInfo.has_speech !== undefined ? chunkInfo.has_speech : true,
      confidence: chunkInfo.confidence || 0.8
    },
    session_info: {
      interview_id: sessionInfo.interview_id || sessionInfo.interviewId,
      candidate_id: sessionInfo.candidate_id || sessionInfo.candidateId,
      session_id: sessionInfo.session_id || sessionInfo.sessionId
    },
    timestamp: new Date().toISOString()
  };
};

/**
 * Create a legacy audio chunk message for backward compatibility
 * @param {string} base64Audio - Base64 encoded audio data
 * @param {string} interviewId - Interview identifier
 * @param {number} sequence - Sequence number
 * @param {boolean} isInterim - Whether this is an interim chunk
 * @returns {Object} Legacy audio chunk message
 */
export const createLegacyAudioChunkMessage = ({
  base64Audio,
  interviewId,
  sequence,
  isInterim = true
}) => {
  return {
    type: "audio_chunk_live",
    interview_id: interviewId,
    audio: base64Audio,
    sequence: sequence,
    is_interim: isInterim,
    timestamp: new Date().toISOString()
  };
};

/**
 * Create optimal audio constraints for Chirp model
 * @param {Object} customConstraints - Custom audio constraints to merge
 * @returns {Object} Optimized audio constraints
 */
export const getOptimalChirpAudioConstraints = (customConstraints = {}) => {
  const baseConstraints = {
    sampleRate: 16000,        // 16kHz - optimal for Chirp
    channelCount: 1,          // Mono
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    latencyHint: 'interactive'
  };

  // Browser-specific optimizations
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(navigator.userAgent);

  let optimizedConstraints = { ...baseConstraints };

  if (isChrome) {
    optimizedConstraints = {
      ...optimizedConstraints,
      googEchoCancellation: true,
      googAutoGainControl: true,
      googNoiseSuppression: true,
      googHighpassFilter: true,
      googTypingNoiseDetection: true,
      googAudioMirroring: false
    };
  } else if (isSafari) {
    optimizedConstraints = {
      ...optimizedConstraints,
      sampleSize: 16,
      channelCount: 1
    };
  } else if (isFirefox) {
    optimizedConstraints = {
      ...optimizedConstraints,
      mozAutoGainControl: true,
      mozNoiseSuppression: true
    };
  }

  return {
    ...optimizedConstraints,
    ...customConstraints
  };
};

/**
 * Validate audio chunk data before sending
 * @param {Object} chunkData - Audio chunk data to validate
 * @returns {Object} Validation result with isValid flag and errors
 */
export const validateAudioChunk = (chunkData) => {
  const errors = [];

  if (!chunkData.audio) {
    errors.push('Missing audio data');
  }

  if (chunkData.sequence === undefined || chunkData.sequence === null) {
    errors.push('Missing sequence number');
  }

  if (!chunkData.format) {
    errors.push('Missing format information');
  } else {
    if (!chunkData.format.encoding) {
      errors.push('Missing audio encoding format');
    }
    if (!chunkData.format.sample_rate) {
      errors.push('Missing sample rate');
    }
    if (!chunkData.format.channels) {
      errors.push('Missing channel count');
    }
  }

  if (!chunkData.session_info || !chunkData.session_info.interview_id) {
    errors.push('Missing interview ID in session info');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate audio quality metrics from PCM data
 * @param {Float32Array} audioData - Raw audio samples
 * @param {number} sampleRate - Sample rate of the audio
 * @returns {Object} Quality metrics
 */
export const calculateAudioQualityMetrics = (audioData, sampleRate = 16000) => {
  if (!audioData || audioData.length === 0) {
    return {
      peak_amplitude: 0,
      rms_level: 0,
      zero_crossing_rate: 0,
      signal_to_noise_ratio: 0,
      dynamic_range: 0
    };
  }

  let peak = 0;
  let rmsSum = 0;
  let zeroCrossings = 0;
  let lastSample = 0;

  for (let i = 0; i < audioData.length; i++) {
    const sample = audioData[i];
    const absSample = Math.abs(sample);
    
    // Peak amplitude
    peak = Math.max(peak, absSample);
    
    // RMS calculation
    rmsSum += sample * sample;
    
    // Zero crossing rate
    if (i > 0 && ((lastSample >= 0 && sample < 0) || (lastSample < 0 && sample >= 0))) {
      zeroCrossings++;
    }
    lastSample = sample;
  }

  const rms = Math.sqrt(rmsSum / audioData.length);
  const zcr = zeroCrossings / audioData.length;

  // Calculate dynamic range
  const sorted = [...audioData].map(Math.abs).sort((a, b) => b - a);
  const dynamicRange = sorted[0] - sorted[Math.floor(sorted.length * 0.9)];

  // Estimate signal-to-noise ratio (simplified)
  const signalLevel = rms;
  const noiseFloor = sorted[Math.floor(sorted.length * 0.8)];
  const snr = signalLevel > 0 && noiseFloor > 0 ? 20 * Math.log10(signalLevel / noiseFloor) : 0;

  return {
    peak_amplitude: peak,
    rms_level: rms,
    zero_crossing_rate: zcr,
    signal_to_noise_ratio: Math.max(0, snr),
    dynamic_range: dynamicRange,
    frequency_estimate: zcr * sampleRate / 2 // Rough frequency estimate
  };
};

/**
 * Create an audio chunk message with automatic format detection
 * @param {Object} params - Parameters for message creation
 * @returns {Object} Audio message in appropriate format
 */
export const createAudioMessage = (params) => {
  const {
    base64Audio,
    sequence,
    chunkInfo,
    sessionInfo,
    format,
    useEnhancedFormat = true,
    interviewId // For legacy support
  } = params;

  // Validate required parameters
  if (!base64Audio) {
    throw new Error('Base64 audio data is required');
  }

  if (sequence === undefined || sequence === null) {
    throw new Error('Sequence number is required');
  }

  // Use enhanced format by default, fallback to legacy if needed
  if (useEnhancedFormat) {
    const message = createEnhancedAudioStreamMessage({
      base64Audio,
      sequence,
      chunkInfo,
      sessionInfo: sessionInfo || { interview_id: interviewId },
      format
    });

    const validation = validateAudioChunk(message);
    if (!validation.isValid) {
      console.warn('⚠️ Enhanced audio message validation failed:', validation.errors);
      // Fallback to legacy format
      return createLegacyAudioChunkMessage({
        base64Audio,
        interviewId: interviewId || sessionInfo?.interview_id,
        sequence,
        isInterim: chunkInfo?.is_interim !== false
      });
    }

    return message;
  } else {
    return createLegacyAudioChunkMessage({
      base64Audio,
      interviewId: interviewId || sessionInfo?.interview_id,
      sequence,
      isInterim: chunkInfo?.is_interim !== false
    });
  }
};

export default {
  createEnhancedAudioStreamMessage,
  createLegacyAudioChunkMessage,
  getOptimalChirpAudioConstraints,
  validateAudioChunk,
  calculateAudioQualityMetrics,
  createAudioMessage
};
